<template>
  <div class="admin-domain-orders container-fluid py-3">
    <div class="d-flex align-items-center justify-content-between flex-wrap gap-2">
      <div>
        <h1 class="h5 mb-0">Domain orders</h1>
        <p class="text-muted small mb-0">
          Buy-through-us operator console. Approve or decline purchase requests,
          set per-TLD retail pricing, and see registered domains. A human approves
          every order — nothing is bought automatically.
        </p>
      </div>
    </div>

    <ul class="nav nav-tabs mt-3">
      <li v-for="t in tabs" :key="t.key" class="nav-item">
        <button class="nav-link" :class="{ active: tab === t.key }" @click="tab = t.key">
          {{ t.label }}<span v-if="t.key === 'queue' && pending.length" class="badge bg-primary ms-1">{{ pending.length }}</span>
          <span v-if="t.key === 'ips' && divergentCount" class="badge bg-warning text-dark ms-1">{{ divergentCount }}</span>
        </button>
      </li>
    </ul>

    <div v-if="error" class="alert alert-danger py-2 small mt-3">{{ error }}</div>

    <!-- Approval queue -->
    <div v-if="tab === 'queue'" class="mt-3">
      <div v-if="!busy.queue && pending.length === 0" class="text-muted small">No orders awaiting approval.</div>
      <div v-for="o in pending" :key="o.id" class="ado-order card mt-2">
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-start flex-wrap gap-2">
            <div>
              <code class="ado-domain">{{ o.domain }}</code>
              <span class="text-muted small ms-2">.{{ o.tld }} · {{ o.tenant_name }}</span>
            </div>
            <div class="ado-order__actions d-flex gap-2">
              <button class="btn btn-success btn-sm" :disabled="busy.act === o.id" @click="approve(o)">
                <CygnusIcon name="spinner" :spin="true" class="me-1" v-if="busy.act === o.id" />
                <CygnusIcon name="check" class="me-1" v-else />Approve &amp; register
              </button>
              <button class="btn btn-outline-danger btn-sm" :disabled="busy.act === o.id" @click="reject(o)">
                <CygnusIcon name="x" class="me-1" />Decline
              </button>
            </div>
          </div>
          <div class="ado-pricing small text-muted mt-2">
            Charged <strong>{{ o.retail_display }}</strong> · snapshot: wholesale
            ${{ fmt(o.wholesale_usd) }} × fx {{ fmt(o.fx_rate_used) }}
            <span v-if="o.end_user_ip">· end-user IP {{ o.end_user_ip }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Pricing table -->
    <div v-if="tab === 'pricing'" class="mt-3">
      <p class="text-muted small">
        Retail = wholesale × fx ({{ fmt(pricingMeta.fx_rate) }}) × margin (default
        {{ fmt(pricingMeta.default_margin) }}), rounded to a .99 price. A per-TLD
        margin or a full retail override wins. <span class="fst-italic">{{ pricingMeta.fx_source }}</span>
      </p>
      <div class="d-flex gap-2 align-items-end mb-2 flex-wrap">
        <div>
          <label class="form-label small text-muted mb-0">Add / edit a TLD</label>
          <input v-model="newTld" type="text" class="form-control form-control-sm" placeholder="co.uk" style="max-width:160px" @keyup.enter="editTld(newTld)">
        </div>
        <button class="btn btn-outline-primary btn-sm" :disabled="!newTld.trim()" @click="editTld(newTld)">Edit</button>
        <button class="btn btn-link btn-sm ms-auto" :disabled="busy.pricing" @click="loadPricing"><CygnusIcon name="refresh" /></button>
      </div>

      <div class="table-responsive">
        <table class="table table-sm align-middle">
          <thead><tr><th>TLD</th><th>Wholesale (USD)</th><th>Margin</th><th>Retail</th><th>Source</th><th></th></tr></thead>
          <tbody>
            <tr v-for="p in pricing" :key="p.tld">
              <td><code>.{{ p.tld }}</code></td>
              <td>{{ p.wholesale_usd !== null ? '$' + fmt(p.wholesale_usd) : '—' }}</td>
              <td>{{ p.margin_mode === 'manual' && p.margin !== null ? fmt(p.margin) + '×' : 'default' }}</td>
              <td><strong>{{ p.retail_display }}</strong></td>
              <td><span class="badge" :class="p.breakdown.source === 'manual_override' ? 'bg-warning text-dark' : 'bg-light text-dark'">{{ p.breakdown.source }}</span></td>
              <td><button class="btn btn-link btn-sm p-0" @click="editTld(p.tld, p)">Edit</button></td>
            </tr>
            <tr v-if="!busy.pricing && pricing.length === 0"><td colspan="6" class="text-muted small">No TLD overrides yet — the default margin applies to every TLD.</td></tr>
          </tbody>
        </table>
      </div>

      <!-- Inline editor -->
      <div v-if="editing" class="card mt-2">
        <div class="card-body">
          <h6 class="mb-2">Pricing for <code>.{{ editing.tld }}</code></h6>
          <div class="row g-2">
            <div class="col-auto">
              <label class="form-label small text-muted mb-0">Wholesale (USD)</label>
              <input v-model="editing.wholesale_usd" type="number" step="0.01" class="form-control form-control-sm" style="max-width:130px">
            </div>
            <div class="col-auto">
              <label class="form-label small text-muted mb-0">Margin mode</label>
              <select v-model="editing.margin_mode" class="form-select form-select-sm" style="max-width:130px">
                <option value="default">Default</option>
                <option value="manual">Manual</option>
              </select>
            </div>
            <div v-if="editing.margin_mode === 'manual'" class="col-auto">
              <label class="form-label small text-muted mb-0">Margin ×</label>
              <input v-model="editing.margin" type="number" step="0.01" class="form-control form-control-sm" style="max-width:110px" placeholder="1.33">
            </div>
            <div class="col-auto">
              <label class="form-label small text-muted mb-0">Retail override (pence)</label>
              <input v-model="editing.retail_pence" type="number" class="form-control form-control-sm" style="max-width:150px" placeholder="blank = calc">
            </div>
          </div>
          <div class="mt-2 d-flex gap-2">
            <button class="btn btn-primary btn-sm" :disabled="busy.save" @click="savePricing"><CygnusIcon name="spinner" :spin="true" class="me-1" v-if="busy.save" />Save</button>
            <button class="btn btn-link btn-sm" @click="editing = null">Cancel</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Registered domains -->
    <div v-if="tab === 'domains'" class="mt-3">
      <div class="d-flex justify-content-end mb-2">
        <button class="btn btn-link btn-sm" :disabled="busy.domains" @click="loadDomains"><CygnusIcon name="refresh" /></button>
      </div>
      <div class="table-responsive">
        <table class="table table-sm align-middle">
          <thead><tr><th>Domain</th><th>Tenant</th><th>Status</th><th>Registrar</th><th>NS mode</th><th>Zone</th></tr></thead>
          <tbody>
            <tr v-for="d in domains" :key="d.id">
              <td><code>{{ d.domain }}</code></td>
              <td class="small">{{ d.tenant_name }}</td>
              <td><span class="badge" :class="statusBadge(d.status)">{{ d.status }}</span></td>
              <td class="small">{{ d.registrar || '—' }}</td>
              <td class="small">{{ d.nameserver_mode }}</td>
              <td class="small">{{ d.zone_ref ? '✓' : '—' }}</td>
            </tr>
            <tr v-if="!busy.domains && domains.length === 0"><td colspan="6" class="text-muted small">No registered domains yet.</td></tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Dedicated IPs (io-domains M3) -->
    <div v-if="tab === 'ips'" class="mt-3">
      <div class="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
        <p class="text-muted small mb-0">
          Account-wide dedicated IPs — Mailgun has no tenant concept for IPs,
          so each one can serve domains across multiple tenants. A divergence
          badge means our records disagree with what Mailgun reports for that
          IP (an assignment made directly in the Mailgun dashboard, or one
          that failed to persist here) — investigate before trusting either
          side blindly.
        </p>
        <div class="d-flex gap-2">
          <button class="btn btn-outline-secondary btn-sm text-nowrap" :disabled="busy.ips" @click="loadIps"><CygnusIcon name="refresh" /></button>
          <button class="btn btn-outline-primary btn-sm text-nowrap" :disabled="busy.requestIp" @click="requestNewIp">
            <CygnusIcon name="spinner" :spin="true" class="me-1" v-if="busy.requestIp" />Request new IP
          </button>
        </div>
      </div>

      <div v-if="requestIpNote" class="alert alert-info py-2 small">{{ requestIpNote }}</div>

      <div v-for="row in ips" :key="row.ip" class="ado-ip card mt-2">
        <div class="card-body py-2">
          <div class="d-flex align-items-center gap-2 flex-wrap">
            <code>{{ row.ip }}</code>
            <span v-if="row.diverges" class="badge bg-warning text-dark">Diverges from Mailgun</span>
            <span v-else class="badge bg-light text-dark">In sync</span>
          </div>
          <div class="small text-muted mt-1">
            Mailgun reports: {{ row.mailgun_domains.length ? row.mailgun_domains.join(', ') : '—' }}
          </div>
          <div class="small text-muted">
            Our records: {{ row.our_assignments.length ? row.our_assignments.map(a => a.mailgun_name).join(', ') : '—' }}
          </div>
        </div>
      </div>
      <div v-if="!busy.ips && ips.length === 0" class="text-muted small">No dedicated IPs on this account yet.</div>
    </div>
  </div>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";

export default {
  name: "AdminDomainOrders",
  props: { componentData: { type: Object, default: () => ({}) } },
  data() {
    return {
      api: useApi(),
      tab: "queue",
      tabs: [
        { key: "queue", label: "Approval queue" },
        { key: "pricing", label: "TLD pricing" },
        { key: "domains", label: "Registered domains" },
        { key: "ips", label: "Dedicated IPs" },
      ],
      pending: [],
      pricing: [],
      pricingMeta: { fx_rate: 0, default_margin: 0, fx_source: "" },
      domains: [],
      editing: null,
      newTld: "",
      error: null,
      busy: { queue: false, act: null, pricing: false, save: false, domains: false, ips: false, requestIp: false },
      ips: [],
      requestIpNote: "",
    };
  },
  computed: {
    divergentCount() { return this.ips.filter((r) => r.diverges).length; },
  },
  mounted() { this.loadQueue(); this.loadIps(); },
  methods: {
    async loadQueue() {
      this.busy.queue = true; this.error = null;
      try {
        const res = await this.api.get("/api/v1/admin/domain-orders?status=pending_approval");
        const payload = res?.data ?? res;
        this.pending = Array.isArray(payload?.data) ? payload.data : [];
      } catch (e) { this.error = this.msg(e, "Failed to load the order queue."); }
      this.busy.queue = false;
    },
    async approve(o) {
      this.busy.act = o.id; this.error = null;
      try {
        const res = await this.api.post(`/api/v1/admin/domain-orders/${o.id}/approve`, {});
        const payload = res?.data ?? res;
        if (payload?.order) this.pending = this.pending.filter((x) => x.id !== o.id);
        else this.error = payload?.error || "Approval failed.";
      } catch (e) { this.error = this.msg(e, "Approval failed."); }
      this.busy.act = null;
    },
    async reject(o) {
      this.busy.act = o.id; this.error = null;
      try {
        const res = await this.api.post(`/api/v1/admin/domain-orders/${o.id}/reject`, {});
        const payload = res?.data ?? res;
        if (payload?.order) this.pending = this.pending.filter((x) => x.id !== o.id);
        else this.error = payload?.error || "Reject failed.";
      } catch (e) { this.error = this.msg(e, "Reject failed."); }
      this.busy.act = null;
    },
    async loadPricing() {
      this.busy.pricing = true; this.error = null;
      try {
        const res = await this.api.get("/api/v1/admin/domain-pricing");
        const payload = res?.data ?? res;
        this.pricing = Array.isArray(payload?.data) ? payload.data : [];
        this.pricingMeta = {
          fx_rate: payload?.fx_rate ?? 0,
          default_margin: payload?.default_margin ?? 0,
          fx_source: payload?.fx_source ?? "",
        };
      } catch (e) { this.error = this.msg(e, "Failed to load pricing."); }
      this.busy.pricing = false;
    },
    editTld(tld, row) {
      const t = String(tld || "").trim().replace(/^\./, "").toLowerCase();
      if (!t) return;
      this.editing = row
        ? { tld: t, wholesale_usd: row.wholesale_usd, margin_mode: row.margin_mode, margin: row.margin, retail_pence: row.retail_pence }
        : { tld: t, wholesale_usd: null, margin_mode: "default", margin: null, retail_pence: null };
      this.newTld = "";
    },
    async savePricing() {
      if (!this.editing) return;
      this.busy.save = true; this.error = null;
      const body = {
        margin_mode: this.editing.margin_mode,
        margin: this.editing.margin_mode === "manual" && this.editing.margin !== "" ? Number(this.editing.margin) : null,
        retail_pence: this.editing.retail_pence === "" || this.editing.retail_pence === null ? null : Number(this.editing.retail_pence),
      };
      if (this.editing.wholesale_usd !== null && this.editing.wholesale_usd !== "") body.wholesale_usd = Number(this.editing.wholesale_usd);
      try {
        await this.api.put(`/api/v1/admin/domain-pricing/${encodeURIComponent(this.editing.tld)}`, body);
        this.editing = null;
        await this.loadPricing();
      } catch (e) { this.error = this.msg(e, "Save failed."); }
      this.busy.save = false;
    },
    async loadDomains() {
      this.busy.domains = true; this.error = null;
      try {
        const res = await this.api.get("/api/v1/admin/managed-domains");
        const payload = res?.data ?? res;
        this.domains = Array.isArray(payload?.data) ? payload.data : [];
      } catch (e) { this.error = this.msg(e, "Failed to load domains."); }
      this.busy.domains = false;
    },
    async loadIps() {
      this.busy.ips = true; this.error = null;
      try {
        const res = await this.api.get("/api/v1/admin/email/ips");
        const payload = res?.data ?? res;
        this.ips = Array.isArray(payload?.data) ? payload.data : [];
      } catch (e) { this.error = this.msg(e, "Failed to load dedicated IPs."); }
      this.busy.ips = false;
    },
    async requestNewIp() {
      this.busy.requestIp = true; this.error = null; this.requestIpNote = "";
      try {
        const res = await this.api.post("/api/v1/admin/email/ips/request-new", {});
        const payload = res?.data ?? res;
        this.requestIpNote = payload?.message || "New IP requested.";
      } catch (e) { this.requestIpNote = this.msg(e, "Could not request a new IP."); }
      this.busy.requestIp = false;
    },
    statusBadge(s) {
      if (s === "registered" || s === "active") return "bg-success";
      if (s === "registering") return "bg-warning text-dark";
      if (s === "transfer_out") return "bg-secondary";
      return "bg-light text-dark";
    },
    fmt(n) { return (Math.round((Number(n) || 0) * 100) / 100).toFixed(2); },
    msg(e, fallback) { return e?.response?.data?.error || e?.message || fallback; },
  },
  watch: {
    tab(t) {
      if (t === "pricing" && this.pricing.length === 0) this.loadPricing();
      if (t === "domains" && this.domains.length === 0) this.loadDomains();
      if (t === "ips" && this.ips.length === 0) this.loadIps();
    },
  },
};
</script>

<style>
.admin-domain-orders .ado-domain { font-size: 0.95rem; }
.admin-domain-orders .nav-tabs .nav-link { cursor: pointer; }
.admin-domain-orders .ado-pricing { border-top: 1px dashed var(--m-border, #E2E8F0); padding-top: 6px; }
</style>
